import React, { Component } from 'react';
import './App.css';
import { Button, Modal, Col, InputGroup, Form } from 'react-bootstrap';
import  DatePicker  from 'react-datepicker';
import { format } from "date-fns";
import { pickerToWallTime, reservationToPicker, detroitNowPicker, reservationDateRange, isReservationTimeAllowed } from './reservationTime';
import "react-datepicker/dist/react-datepicker.css";
import AdminLogin from './AdminLogin';
import KeyboardAwareness from './KeyboardAwareness';

const serverUrl = "https://www.evergreen-restaurant.us/serverv3";
const ADMIN_SESSION_KEY = 'evergreen.table-management.admin-session';
const SESSION_RENEW_INTERVAL_MS = 24 * 60 * 60 * 1000;

const readStoredAdminSession = () => {
  try {
    const session = JSON.parse(window.localStorage.getItem(ADMIN_SESSION_KEY) || 'null');
    return session && typeof session.token === 'string' && session.token ? session : null;
  } catch (_error) {
    window.localStorage.removeItem(ADMIN_SESSION_KEY);
    return null;
  }
};

const AppIcon = ({ name }) => {
  let artwork;
  switch (name) {
    case 'calendar':
      artwork = <><rect x="3" y="5" width="18" height="16" rx="3"/><path d="M8 3v4M16 3v4M3 10h18"/></>;
      break;
    case 'clock':
      artwork = <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>;
      break;
    case 'check':
      artwork = <><circle cx="12" cy="12" r="9"/><path d="m8 12 2.5 2.5L16 9"/></>;
      break;
    case 'layers':
      artwork = <><path d="m12 3 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5M3 16l9 5 9-5"/></>;
      break;
    case 'reset':
      artwork = <><path d="M4 7v5h5"/><path d="M5.5 16a8 8 0 1 0 .5-9L4 12"/></>;
      break;
    case 'logout':
      artwork = <><path d="M10 5H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h5"/><path d="m14 8 4 4-4 4M18 12H8"/></>;
      break;
    case 'plus':
      artwork = <><path d="M12 5v14M5 12h14"/></>;
      break;
    case 'eye':
      artwork = <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"/><circle cx="12" cy="12" r="2.5"/></>;
      break;
    case 'edit':
      artwork = <><path d="M4 20h4l11-11-4-4L4 16v4Z"/><path d="m13.5 6.5 4 4"/></>;
      break;
    case 'message':
      artwork = <><path d="M20 15a3 3 0 0 1-3 3H9l-5 3v-6a3 3 0 0 1-1-2V7a3 3 0 0 1 3-3h11a3 3 0 0 1 3 3v8Z"/><path d="M8 9h8M8 13h5"/></>;
      break;
    case 'users':
      artwork = <><circle cx="9" cy="8" r="3"/><path d="M3 20v-2a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v2"/><path d="M16 4a3 3 0 0 1 0 6M17 13a5 5 0 0 1 4 5v2"/></>;
      break;
    case 'table':
      artwork = <><rect x="3" y="6" width="18" height="8" rx="2"/><path d="M6 14v5M18 14v5M8 10h8"/></>;
      break;
    case 'warning':
      artwork = <><path d="M10.3 4.2 2.6 18a2 2 0 0 0 1.8 3h15.2a2 2 0 0 0 1.8-3L13.7 4.2a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4M12 17h.01"/></>;
      break;
    case 'info':
      artwork = <><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/></>;
      break;
    default:
      artwork = <><rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/><rect x="3" y="14" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/></>;
  }
  return (
    <svg className="tm-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {artwork}
    </svg>
  );
};

const getTableGroupKey = (table = '') => {
  if (table.indexOf('Bar') === 0) return 'Bar';
  const prefix = table.charAt(0);
  return ['A', 'B', 'C', 'D', 'O'].indexOf(prefix) >= 0 ? prefix : 'A';
};

const TableSelector = ({ groups, activeGroupKey, onGroupChange, selectedTable, onSelectTable, idPrefix }) => {
  const activeGroup = groups.find((group) => group.key === activeGroupKey) || groups[0];
  const hasSelection = selectedTable && selectedTable !== 'Choose a Table';

  return (
    <section className="tm-table-selector" aria-labelledby={`${idPrefix}-table-title`}>
      <div className="tm-table-selector__heading">
        <span className="tm-form-step">1</span>
        <span className="tm-table-selector__copy">
          <small>Table assignment</small>
          <strong id={`${idPrefix}-table-title`}>Choose a table</strong>
        </span>
        <span className={`tm-table-selection ${hasSelection ? 'is-selected' : ''}`} aria-live="polite">
          <AppIcon name={hasSelection ? 'check' : 'table'} />
          {hasSelection ? `Table ${selectedTable}` : 'None selected'}
        </span>
      </div>

      <div className="tm-table-zones" role="tablist" aria-label="Table areas">
        {groups.map((group) => (
          <button
            key={group.key}
            type="button"
            id={`${idPrefix}-tab-${group.key}`}
            className={`tm-table-zone ${activeGroup.key === group.key ? 'is-active' : ''}`}
            role="tab"
            aria-selected={activeGroup.key === group.key}
            aria-controls={`${idPrefix}-panel`}
            onClick={() => onGroupChange(group.key)}
          >
            <strong>{group.shortLabel}</strong>
            <small>{group.tables.length}</small>
          </button>
        ))}
      </div>

      <div
        className="tm-table-zone-panel"
        id={`${idPrefix}-panel`}
        role="tabpanel"
        aria-labelledby={`${idPrefix}-tab-${activeGroup.key}`}
      >
        <div className="tm-table-zone-panel__heading">
          <span><AppIcon name="table" /></span>
          <strong>{activeGroup.label}</strong>
          <small>Tap a table number to select it</small>
        </div>
        <div className="tm-table-choice-grid">
          {activeGroup.tables.map((table) => (
            <button
              key={table}
              type="button"
              className={`tm-table-choice ${selectedTable === table ? 'is-selected' : ''}`}
              aria-pressed={selectedTable === table}
              onClick={() => onSelectTable(table)}
            >
              <span>{table}</span>
              {selectedTable === table ? <AppIcon name="check" /> : null}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

const QUICK_GUEST_COUNTS = [1, 2, 3, 4, 5, 6, 7, 8];

const GuestCountPicker = ({ value, onChange, id, compact = false }) => {
  const normalizedValue = String(value || '');

  return (
    <div className={`tm-guest-count-picker ${compact ? 'tm-guest-count-picker--compact' : ''}`}>
      <div className="tm-guest-quick-picks" role="group" aria-label="Quick guest count">
        {QUICK_GUEST_COUNTS.map((count) => {
          const isSelected = normalizedValue === String(count);
          return (
            <button
              key={count}
              type="button"
              className={isSelected ? 'is-selected' : ''}
              aria-pressed={isSelected}
              aria-label={`${count} ${count === 1 ? 'guest' : 'guests'}`}
              onClick={() => onChange(String(count))}
            >
              {count}
            </button>
          );
        })}
      </div>
      <div className="tm-guest-count-control tm-guest-count-control--manual">
        <span><AppIcon name="edit" /></span>
        <Form.Control
          id={id}
          aria-label="Manual guest count"
          placeholder="Other count"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          type="number"
          min="1"
          inputMode="numeric"
        />
        <em>Manual</em>
      </div>
    </div>
  );
};

class App extends Component {

  constructor(props) {
    super(props);

    this.state = {
      show: false,
      sectionReady: false,
      addShow: false,
      curSection: '1',
      tableOption: 'Choose a Table',
      addTableZone: 'A',
      peopleEnter: '',
      showDropList: false,
      selectedSection: '',
      selectedId: '',
      showEditModal: false,
      editShow: false,
      peopleEditEnter: '',
      tableTime: '',
      tableStatus: '',
      selectTableEditId: '',
      selectedTableEdit: '',
      editTableZone: 'A',
      showActivateTableOnly: false,
      showNewReservationModal: false,
      showWaitlist: false,
      reservationName: '',
      reservationSize: '',
      reservationPhone: '',
      reservationNote: '',
	      selectedData: detroitNowPicker(),
	      waitlistSelectedData: detroitNowPicker(),
	      reservationList: [],
	      showEditReservationModal: false, 
      editReservationName: '',
      editReservationSize: '',
      editReservationPhone: '',
      editReservationNote: '',
      editSelectedData: detroitNowPicker(),
      showReservationStatusDropList: false,
      editReservationStatus: '',
      editReservationId: '',
	      showEditWaitlistModal: false,
	      editWaitlistId: '',
	      editWaitlistName: '',
	      editWaitlistSize: '',
	      editWaitlistPhone: '',
      editWaitlistNote: '',
      editWaitlistSelectedData: detroitNowPicker(),
      editWaitlistStatus: '',
      showWaitlistStatusDropList: false,
      adminSession: readStoredAdminSession(),
      uiDialog: null,
      dialogValue: '',
      dialogError: '',
      dialogBusy: false,
      toast: null,
    };
  }

  componentDidMount(){
    KeyboardAwareness.install();

    let section = localStorage.getItem('section');

    if(section == null){
      this.setState({show: true});
    }else{
      this.setState({sectionReady: true});
    }

	    if (this.state.adminSession && this.state.adminSession.token) {
	      this.loadReservation();
	      this.startAuthenticatedTimers();
	      this.renewAdminSession();
	    }

  }

	  componentWillUnmount() {
	    KeyboardAwareness.uninstall();
	    clearInterval(this.interval);
	    clearInterval(this.sessionRenewInterval);
	    clearTimeout(this.toastTimer);
	  }

  startAuthenticatedTimers = () => {
    clearInterval(this.interval);
    clearInterval(this.sessionRenewInterval);
    this.interval = setInterval(() => this.loadReservation(), 60000);
    this.sessionRenewInterval = setInterval(
      () => this.renewAdminSession(),
      SESSION_RENEW_INTERVAL_MS,
    );
  }

  persistAdminSession = (adminSession) => {
    window.localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(adminSession));
  }

  handleAuthenticated = (adminSession) => {
    this.persistAdminSession(adminSession);
    this.setState({ adminSession }, () => {
      this.loadReservation();
      this.startAuthenticatedTimers();
    });
  }

  clearAdminSession = () => {
    window.localStorage.removeItem(ADMIN_SESSION_KEY);
    // Remove sessions created by the short-lived implementation that preceded
    // persistent table-management login.
    window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
    clearInterval(this.interval);
    clearInterval(this.sessionRenewInterval);
    this.setState({ adminSession: null, reservationList: [] });
  }

  renewAdminSession = async () => {
    if (this.sessionRenewPromise) return this.sessionRenewPromise;
    const token = this.state.adminSession && this.state.adminSession.token;
    if (!token) return false;

    this.sessionRenewPromise = (async () => {
      try {
        const response = await fetch(`${serverUrl}/admin/session/renew`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Client-Platform': 'manager',
            'X-Client-Version': '0.2.0',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ keepSignedIn: true }),
        });
        if (response.status === 401) {
          this.clearAdminSession();
          return false;
        }
        if (!response.ok) return false;

        const renewedSession = await response.json().catch(() => null);
        if (!renewedSession || !renewedSession.token) return false;
        this.persistAdminSession(renewedSession);
        this.setState({ adminSession: renewedSession });
        return true;
      } catch (_error) {
        // A temporary network outage must not discard a still-valid session.
        return false;
      } finally {
        this.sessionRenewPromise = null;
      }
    })();

    return this.sessionRenewPromise;
  }

  authenticatedFetch = async (url, options = {}) => {
    const token = this.state.adminSession && this.state.adminSession.token;
    if (!token) throw new Error('Admin login required.');
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Client-Platform': 'manager',
        'X-Client-Version': '0.2.0',
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 401) this.clearAdminSession();
    return response;
  }

	  getReservationRows = () => {
	    return this.state.reservationList.filter((reservation) => reservation.type == '1');
	  }

	  getWaitlistRows = () => {
	    return this.state.reservationList.filter((reservation) => reservation.type == '2');
	  }

  handleModal = ()=>{
    this.setState({
      show: this.state.show?false:true
    })
  }

  showToast = (message, variant = 'info', title = '') => {
    clearTimeout(this.toastTimer);
    const defaultTitles = {
      success: 'Done',
      error: 'Something needs attention',
      warning: 'Check this',
      info: 'Heads up',
    };
    this.setState({
      toast: {
        id: Date.now(),
        message,
        variant,
        title: title || defaultTitles[variant] || defaultTitles.info,
      },
    });
    this.toastTimer = setTimeout(() => this.setState({ toast: null }), 4200);
  }

  dismissToast = () => {
    clearTimeout(this.toastTimer);
    this.setState({ toast: null });
  }

  openDialog = (dialog) => {
    this.setState({
      uiDialog: dialog,
      dialogValue: dialog.initialValue === undefined ? '' : String(dialog.initialValue),
      dialogError: '',
      dialogBusy: false,
    });
  }

  closeDialog = () => {
    if (this.state.dialogBusy) return;
    this.setState({
      uiDialog: null,
      dialogValue: '',
      dialogError: '',
      dialogBusy: false,
    });
  }

  handleDialogValue = (event) => {
    this.setState({ dialogValue: event.target.value, dialogError: '' });
  }

  confirmDialog = async () => {
    const { uiDialog, dialogValue, dialogBusy } = this.state;
    if (!uiDialog || dialogBusy) return;

    this.setState({ dialogBusy: true, dialogError: '' });
    try {
      const shouldClose = await uiDialog.onConfirm(dialogValue);
      if (shouldClose === false) {
        this.setState({ dialogBusy: false });
        return;
      }
      this.setState({
        uiDialog: null,
        dialogValue: '',
        dialogError: '',
        dialogBusy: false,
      });
    } catch (error) {
      this.setState({
        dialogBusy: false,
        dialogError: error && error.message
          ? error.message
          : 'Something went wrong. Please try again.',
      });
    }
  }


  setSection = (count)=>{
    localStorage.setItem('section', count);
    this.handleModal();

    this.generateData()

    this.setState({sectionReady: true});

  }

  generateData = ()=>{
    let sectionCount = localStorage.getItem('section');

    console.log('There are ' + sectionCount + ' sections!');

    for(let i =1; i<=sectionCount; i++){
      localStorage.setItem(i, JSON.stringify({'name':'','data':[]}));
    }

  }

  addSection = ()=>{
    this.openDialog({
      kind: 'input',
      icon: 'layers',
      eyebrow: 'Floor setup',
      title: 'Add sections',
      message: 'Create one or more new service sections on today\'s floor.',
      inputLabel: 'Number of sections',
      inputType: 'number',
      placeholder: 'For example, 2',
      confirmLabel: 'Add sections',
      onConfirm: (value) => {
        let newSection = value - 0;

        if(!Number.isInteger(newSection) || newSection <= 0){
          this.setState({ dialogError: 'Enter a whole number greater than zero.' });
          return false;
        }

        let sectionCount = localStorage.getItem('section')-0;
        localStorage.setItem('section', sectionCount + newSection);

        console.log('Add ' + newSection + ' sections!');

        for(let i =newSection; i>0; --i){
          localStorage.setItem(sectionCount+i, JSON.stringify({'name':'','data':[]}));
        }

        this.setState({sectionReady: true});
        this.showToast(
          `${newSection} ${newSection === 1 ? 'section' : 'sections'} added to the floor.`,
          'success',
          'Floor updated',
        );
        return true;
      },
    });
  }

  setName = (section) =>{
    let currentSectionData = JSON.parse(localStorage.getItem(section));
    this.openDialog({
      kind: 'input',
      icon: 'edit',
      eyebrow: `Section ${section}`,
      title: 'Name this section',
      message: 'Use a short, recognizable name for the host team.',
      inputLabel: 'Section name',
      inputType: 'text',
      placeholder: 'For example, Main dining room',
      initialValue: currentSectionData.name,
      confirmLabel: 'Save name',
      onConfirm: (person) => {
        this.setState({sectionReady:false});

        let tmpSectionData = JSON.parse(localStorage.getItem(section));
        let newSectionData = {'name': person, 'data': tmpSectionData.data};
        localStorage.setItem(section, JSON.stringify(newSectionData));

        this.setState({sectionReady:true});
        this.showToast(`Section ${section} name saved.`, 'success');
        return true;
      },
    });
  }

  handleAddModel = (section)=>{
    if (section === '' || section == null) {
      // Close only — never toggles, so a stale-state double call cannot
      // reopen the modal before the fade-out transition has finished.
      this.setState({ addShow: false });
      return;
    }
    this.setState({
      addShow: true,
      curSection: section,
      tableOption: 'Choose a Table',
      addTableZone: 'A',
      peopleEnter: '',
      showDropList: false,
    });
  }

  handleEditModel = (section)=>{
    if (section === '' || section == null) {
      this.setState({ editShow: false });
      return;
    }
    this.setState({
      editShow: true,
      curSection: section,
      peopleEditEnter: '',
      tableTime: '',
      tableStatus: '',
      selectTableEditId: '',
      selectedTableEdit: '',
    });
  }

  addRecord = ()=>{

    if(this.state.curSection == null){
      this.showToast('Refresh the page and try again.', 'error', 'Section unavailable');
      return;
    }

    if(this.state.tableOption == 'Choose a Table'){
      this.showToast('Choose a table before seating this party.', 'warning', 'Table required');
      return;
    }

    let peopleNumber = this.state.peopleEnter-0;

    if(!Number.isInteger(peopleNumber) || peopleNumber <= 0){
      this.showToast('Enter a whole number greater than zero.', 'warning', 'Guest count required');
      return;
    }

    this.setState({sectionReady:false});

    let tmpSectionData = JSON.parse(localStorage.getItem(this.state.curSection));

    let tmpSectionGroup = tmpSectionData.data.length>0?JSON.parse(tmpSectionData.data):tmpSectionData.data;

    tmpSectionGroup.push({'id':tmpSectionGroup.length, 'count':peopleNumber, 'table': this.state.tableOption, statu: 'active', 'time':new Date().toLocaleTimeString() });

    let newSectionData = {'name': tmpSectionData.name, 'data': JSON.stringify(tmpSectionGroup)};

    localStorage.setItem(this.state.curSection, JSON.stringify(newSectionData));

    //console.log(localStorage.getItem(this.state.curSection).name);
    this.setState({
      sectionReady:true
    });

    this.handleAddModel('');
  }

  editRecord = (data, singleData)=>{
    //data.section, singleData.id
    console.log(data);
    console.log(singleData);

    this.setState({
      selectedSection: data.section,
      selectedId: singleData.id,
      editShow: true,
      peopleEditEnter: singleData.count,
      selectedTableEdit: singleData.table,
      editTableZone: getTableGroupKey(singleData.table),
      tableTime: singleData.time,
      tableStatus: singleData.statu,
      selectTableEditId: singleData.id,
    })
  }


  editRecordAction = ()=>{
      let record = JSON.parse(localStorage.getItem(this.state.selectedSection));

      let JsonData = JSON.parse(record.data);

      for(let i=0; i<JsonData.length; i++){
        if(JsonData[i].id == this.state.selectedId){
          JsonData[i].table = this.state.selectedTableEdit;
          JsonData[i].statu = this.state.tableStatus;
          JsonData[i].count = this.state.peopleEditEnter;
          break;
        }
      }

      record.data = JSON.stringify(JsonData);

      localStorage.setItem(this.state.selectedSection, JSON.stringify(record));
      this.setState({sectionReady:true});

      this.setState({
        selectedSection: '',
        selectedId: '',
        editShow: false,
        peopleEditEnter: '',
        selectedTableEdit: '',
        tableTime: '',
        tableStatus: '',
        selectTableEditId: '',
      })
  }


	  sendGuestNotice = async (dataJson) => {
	    try {
	      const response = await this.authenticatedFetch(`${serverUrl}/orders/reservationnotice`,{
	        method: 'POST',
	        headers:{
	          'Accept': 'application/json',
	          'Content-Type': 'application/json',
	        },
	        body: JSON.stringify(dataJson),
	      });
	      const res = await response.json();
	      if(res.success === true){
	        this.showToast('The table-ready message was sent to the customer.', 'success', 'Message sent');
	      }else{
	        this.showToast('The message could not be sent. Please try again.', 'error', 'Message not sent');
	      }
	      this.loadReservation();
	    } catch (_error) {
	      this.showToast('Check the connection and try again.', 'error', 'Message not sent');
	    }
	    return true;
	  }

	  openGuestNoticeDialog = (id, name, phone, size) => {
	    this.openDialog({
	      kind: 'confirm',
	      icon: 'message',
	      eyebrow: 'Guest notification',
	      title: 'Send table-ready message?',
	      message: `${name || 'This customer'} will receive a message that their table is ready.`,
	      confirmLabel: 'Send message',
	      onConfirm: () => this.sendGuestNotice({ id, name, phone, size }),
	    });
	  }

	  messageWaitlist = (id, name, phone, size)=>{
	    this.openGuestNoticeDialog(id, name, phone, size);
	  }

  messageReservation = (id, name, phone, size)=>{
    this.openGuestNoticeDialog(id, name, phone, size);
  }

  completeAllActive = ()=>{
    this.openDialog({
      kind: 'confirm',
      icon: 'check',
      eyebrow: 'End of service',
      title: 'Complete all active tables?',
      message: 'Every active table on the floor will be marked complete. Voided tables will stay unchanged.',
      confirmLabel: 'Complete all',
      onConfirm: () => {
        this.setState({ sectionReady:false });

        let totalSection = parseInt(localStorage.getItem('section'));

        for(let i = 1; i<=totalSection; i++){
          let record = JSON.parse(localStorage.getItem(i+''));

          if(record.data.length == 0)
            continue;

          let JsonData = JSON.parse(record.data);

          for(let i=0; i<JsonData.length; i++){
            if(JsonData[i].statu != 'void')
              JsonData[i].statu = 'complete';
          }

          record.data = JSON.stringify(JsonData);
          localStorage.setItem(i+'', JSON.stringify(record));
        }

        this.setState({ sectionReady:true });
        this.showToast('All active tables are now complete.', 'success', 'Floor updated');
        return true;
      },
    });
  }

  getFloorSummary = () => {
    const summary = {
      sections: Number(localStorage.getItem('section')) || 0,
      activeTables: 0,
      activeGuests: 0,
      completedGuests: 0,
    };

    for (let section = 1; section <= summary.sections; section += 1) {
      try {
        const storedSection = JSON.parse(localStorage.getItem(String(section)) || 'null');
        if (!storedSection || !storedSection.data) continue;
        const records = typeof storedSection.data === 'string'
          ? JSON.parse(storedSection.data)
          : storedSection.data;
        (Array.isArray(records) ? records : []).forEach((record) => {
          const guestCount = Number(record.count) || 0;
          if (record.statu === 'active') {
            summary.activeTables += 1;
            summary.activeGuests += guestCount;
          }
          if (record.statu === 'complete') summary.completedGuests += guestCount;
        });
      } catch (_error) {
        // Keep the dashboard usable if a legacy local record is malformed.
      }
    }
    return summary;
  }

  generateSectionData = ()=>{
    let sectionCount = localStorage.getItem('section');

    let dataSet = [];

    for(let i =1; i<=sectionCount; i++){
      let tmpData = JSON.parse(localStorage.getItem(i));
      dataSet.push({'section':i, 'name': tmpData.name, 'data': tmpData.data});
    }

    return dataSet.map((data) =>{

      let curData = data.data.length>0?JSON.parse(data.data):data.data;

      let totalA =0;
      let totalC =0;

      for(let j=0; j<curData.length; j++){
        if(curData[j].statu == 'active')
          totalA += parseInt(curData[j].count);

        if(curData[j].statu == 'complete')
          totalC += parseInt(curData[j].count);
      }

      const visibleRecords = this.state.showActivateTableOnly
        ? curData.filter((record) => record.statu === 'active')
        : curData;

      const totalGuests = totalA + totalC;

      return(
        <section className="tm-section-card" key={data.section}>
          <div className="tm-section-card__header">
            <button type="button" className="tm-section-name" title={data.name ? data.name : 'Set section name'} onClick={()=>this.setName(data.section)}>
              <span className="tm-section-index">{String(data.section).padStart(2, '0')}</span>
              <span>
                <small>Section {data.section}</small>
                <strong>{data.name ? data.name : 'Set section name'}</strong>
              </span>
            </button>
            <span className="tm-section-total" title="Guests on this section (active + completed)">
              <strong>{totalGuests}</strong><small>guests</small>
            </span>
            <button type="button" className="tm-icon-button tm-add-table" onClick={()=>{this.handleAddModel(data.section)}} aria-label={`Add party to section ${data.section}`}>
              <AppIcon name="plus" />
            </button>
          </div>

          <div className="tm-section-metrics" aria-label="Section detail counts">
            <span><i className="tm-dot tm-dot--active" />A <strong>{totalA}</strong></span>
            <span><i className="tm-dot tm-dot--complete" />C <strong>{totalC}</strong></span>
          </div>

          <div className="tm-table-list">
            {visibleRecords.length ? visibleRecords.map((singleData) => {
              const status = singleData.statu || 'active';
              return (
                <button
                  type="button"
                  key={singleData.id}
                  className={`tm-table-row tm-table-row--${status}`}
                  onClick={()=>{this.editRecord(data, singleData)}}
                >
                  <span className="tm-table-badge">{singleData.table}</span>
                  <span className="tm-table-copy">
                    <strong>{singleData.count} {Number(singleData.count) === 1 ? 'Guest' : 'Guests'}</strong>
                    <small>{singleData.time}</small>
                  </span>
                  <span className="tm-status-pill">{status}</span>
                  <AppIcon name="edit" />
                </button>
              );
            }) : (
              <div className="tm-empty-section">
                <span><AppIcon name="users" /></span>
                <strong>{this.state.showActivateTableOnly ? 'No active tables' : 'Ready for service'}</strong>
                <small>Tap + to seat a party</small>
              </div>
            )}
          </div>
        </section>
      );
    });

  }

  renderGuestRows = (rows, listType) => {
    const visibleRows = this.state.showActivateTableOnly
      ? rows.filter((reservation) => reservation.status === 'active' || reservation.status === 'noticed')
      : rows;
    if (!visibleRows.length) {
      return (
        <div className="tm-list-empty">
          <span><AppIcon name={listType === 'reservation' ? 'calendar' : 'clock'} /></span>
          <strong>No {listType === 'reservation' ? 'reservations' : 'waitlist parties'} to show</strong>
          <small>New entries will appear here automatically.</small>
        </div>
      );
    }

    return visibleRows.map((reservation) => {
      const status = String(reservation.status || 'active').toLowerCase();
      const editEntry = listType === 'reservation' ? this.EditReservation : this.EditWaitlist;
      const messageEntry = listType === 'reservation' ? this.messageReservation : this.messageWaitlist;
      const canMessage = status === 'active' || status === 'noticed';
      return (
        <article className={`tm-guest-row tm-guest-row--${status}`} key={`${listType}-${reservation.ID}`}>
          <div className="tm-guest-time">
            <span>{format(reservationToPicker(reservation.time), "MMM d")}</span>
            <strong>{format(reservationToPicker(reservation.time), "h:mm aa")}</strong>
          </div>
          <div className="tm-guest-details">
            <div className="tm-guest-title">
              <strong>{reservation.name}</strong>
              <span className="tm-status-pill">{status}</span>
            </div>
            <div className="tm-guest-meta">
              <span><AppIcon name="users" />{reservation.size} people</span>
              <span>{reservation.phone}</span>
            </div>
            <p>{reservation.note || 'No note added'}</p>
          </div>
          <div className="tm-guest-actions">
            <button type="button" className="tm-icon-button" onClick={()=>editEntry(reservation)} aria-label={`Edit ${reservation.name}`}>
              <AppIcon name="edit" />
            </button>
            {canMessage ? (
              <button
                type="button"
                className={`tm-icon-button ${status === 'noticed' ? 'tm-icon-button--done' : 'tm-icon-button--message'}`}
                onClick={()=>status === 'noticed'
                  ? this.showToast('This customer has already received a table-ready message.', 'info', 'Already notified')
                  : messageEntry(reservation.ID, reservation.name, reservation.phone, reservation.size)}
                aria-label={status === 'noticed' ? 'Customer already notified' : `Notify ${reservation.name}`}
              >
                <AppIcon name={status === 'noticed' ? 'check' : 'message'} />
              </button>
            ) : null}
          </div>
        </article>
      );
    });
  }

  resetApp = ()=>{
    this.openDialog({
      kind: 'danger',
      icon: 'warning',
      eyebrow: 'Permanent action',
      title: 'Reset the entire floor?',
      message: 'This removes the current floor setup and waitlist data from this device. This action cannot be undone.',
      confirmLabel: 'Reset floor',
      onConfirm: () => {
        localStorage.removeItem('section');
        localStorage.removeItem('waitlist');
        this.handleModal();
        this.showToast('The floor is ready to be set up again.', 'success', 'Floor reset');
        return true;
      },
    });
  }

  handleNewAppointmentModel = (open = true)=>{
    this.setState({
      showNewReservationModal: open ? true : false,
      reservationName: '',
      reservationSize: '',
      reservationPhone: '',
      reservationNote:'',
      selectedData: detroitNowPicker(),
    })
  }

  handleNewWaitlistModel = (open = true)=>{
    this.setState({
      showNewWaitlistModal: open ? true : false,
      waitlistName: '',
      waitlistSize: '',
      waitlistPhone: '',
      waitlistNote:'',
      waitlistSelectedData: detroitNowPicker(),
    })
  }

  loadReservation = ()=>{

	    this.authenticatedFetch(`${serverUrl}/orders/reservation?curLocation=1`,{
        method: 'get',
        headers:{
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }, 
        })

    .then((response) => response.json())
    .then((res) => {
      //this.toastNotify('here');
      if(res.success === true){
        //console.log(res.result);
        this.setState({reservationList: res.result});
      }else{
        //console.log(res.message);
      }

    })
    .catch((err)=>{
      //console.log(err);
    })

  }


  handleNewAppointmentAction = ()=>{

    if(this.state.reservationName.trim() == '' || this.state.reservationSize.trim() == '' || this.state.reservationPhone.trim() == ''){
      this.showToast('Add the guest name, group size, and phone number.', 'warning', 'Reservation details required');
      return;
    }

	    let reservationJson = {
	      selectedLocation: '1',
	      selectedType: '1',
	      reservationName: this.state.reservationName,
	      reservationSize:this.state.reservationSize, 
	      reservationPhone:this.state.reservationPhone,
      selectedData:pickerToWallTime(this.state.selectedData),
      reservationNote:this.state.reservationNote,
      status: 'active',
      sendCustomerEditLink: true,
    }

    this.authenticatedFetch(`${serverUrl}/orders/reservation`,{
        method: 'POST',
        headers:{
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reservationJson)   
        })

    .then((response) => response.json())
    .then((res) => {
      //this.toastNotify('here');
      if(res.success === true){

       //console.log(res.message);

       this.setState({
        showNewReservationModal: false,
        reservationName: '',
        reservationSize: '',
        reservationPhone: '',
        reservationNote:'',
      })

      this.loadReservation();
      

      }else{
        //console.log(res.message);
      }

    })
    .catch((err)=>{
      //console.log(err);
    })

  }



	  handleNewWaitlistAction = ()=>{

	    if(this.state.waitlistName.trim() == '' || this.state.waitlistSize.trim() == '' || this.state.waitlistPhone.trim() == ''){
	      this.showToast('Add the guest name, group size, and phone number.', 'warning', 'Waitlist details required');
	      return;
	    }

	    let waitListJson = {
	      selectedLocation: '1',
	      selectedType: '2',
	      reservationName: this.state.waitlistName,
	      reservationSize:this.state.waitlistSize, 
	      reservationPhone:this.state.waitlistPhone,
	      selectedData:pickerToWallTime(this.state.waitlistSelectedData),
	      reservationNote:this.state.waitlistNote,
	      status: 'active',
	      sendCustomerEditLink: true,
	    };

	    this.authenticatedFetch(`${serverUrl}/orders/reservation`,{
	        method: 'POST',
	        headers:{
	          'Accept': 'application/json',
	          'Content-Type': 'application/json',
	        },
	        body: JSON.stringify(waitListJson)   
	        })
	    .then((response) => response.json())
	    .then((res) => {
	      if(res.success === true){
	        this.setState({
	          showNewWaitlistModal: false,
	          waitlistName: '',
	          waitlistSize: '',
	          waitlistPhone: '',
	          waitlistNote:'',
	        })

	        this.loadReservation();
	      }
	    })
	    .catch((err)=>{
	      //console.log(err);
	    })

	  }

  toggleReservationModal = ()=>{
    this.setState({showReservations: this.state.showReservations?false:true})
  }

  toggleEditReservationModal = ()=>{
    this.setState({showEditReservationModal: this.state.showEditReservationModal?false:true})
  }

  toggleWaitlistModal = async ()=>{
    this.setState({
      showWaitlist: this.state.showWaitlist?false:true
    })
  }

  toggleEditWaitlistModal = ()=>{
    this.setState({showEditWaitlistModal: this.state.showEditWaitlistModal?false:true})
  }


  EditReservation = (reservation)=>{
    this.toggleEditReservationModal();

    this.setState({
      editReservationId: reservation.ID,
      editReservationName: reservation.name,
      editReservationSize: reservation.size,
      editReservationPhone: reservation.phone,
      editReservationNote: reservation.note,
      editSelectedData: reservationToPicker(reservation.time),
      editReservationStatus: reservation.status,
    })
  }


	  EditWaitlist = (waitlist)=>{
	    this.toggleEditWaitlistModal();

	    this.setState({
	      editWaitlistId: waitlist.ID,
	      editWaitlistName: waitlist.name,
	      editWaitlistSize: waitlist.size,
	      editWaitlistPhone: waitlist.phone,
	      editWaitlistNote: waitlist.note,
	      editWaitlistSelectedData: reservationToPicker(waitlist.time),
	      editWaitlistStatus: waitlist.status,
	    })

	  }

	  updateWaitlist = () =>{

	    if(this.state.editWaitlistName.trim() == '' || this.state.editWaitlistSize.trim() == '' || this.state.editWaitlistPhone.trim() == ''){
	      this.showToast('Add the guest name, group size, and phone number.', 'warning', 'Waitlist details required');
	      return;
	    }

	    let waitListJson = {
	      ID: this.state.editWaitlistId,
	      name: this.state.editWaitlistName,
	      size:this.state.editWaitlistSize, 
	      phone:this.state.editWaitlistPhone,
	      time:pickerToWallTime(this.state.editWaitlistSelectedData),
	      note:this.state.editWaitlistNote,
	      status: this.state.editWaitlistStatus,
	    };

	    this.authenticatedFetch(`${serverUrl}/orders/editreservation`,{
	        method: 'POST',
	        headers:{
	          'Accept': 'application/json',
	          'Content-Type': 'application/json',
	        },
	        body: JSON.stringify(waitListJson)   
	        })
	    .then((response) => response.json())
	    .then((res) => {
	      if(res.success === true){
	        this.loadReservation();
	      }

	      this.toggleEditWaitlistModal();
	    })
	    .catch((err)=>{
	      //console.log(err);
	    })

	  }

  updateReservation = ()=>{

    let dataJson = {
      ID: this.state.editReservationId,
      name: this.state.editReservationName,
      size: this.state.editReservationSize,
      phone: this.state.editReservationPhone,
      note: this.state.editReservationNote,
      time: pickerToWallTime(this.state.editSelectedData),
      status: this.state.editReservationStatus,
    }


    this.authenticatedFetch(`${serverUrl}/orders/editreservation`,{
        method: 'POST',
        headers:{
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataJson)   
        })

    .then((response) => response.json())
    .then((res) => {
      //this.toastNotify('here');
      if(res.success === true){

      this.loadReservation();

      this.toggleEditReservationModal();

      }else{
        //console.log(res.message);
      this.toggleEditReservationModal();

      }

    })
    .catch((err)=>{
      //console.log(err);
    })



  }



  render() {

        if (!this.state.adminSession || !this.state.adminSession.token) {
          return <AdminLogin serverUrl={serverUrl} onAuthenticated={this.handleAuthenticated} />;
        }

        const tables_A = ['A1','A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8'];
        const tables_B = ['B1','B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'B8', 'B9'];
        const tables_C = ['C1', 'C2', 'C3', 'C4', 'C5', 'C6'];
        // const tables_R = ['R1', 'R2'];
        const tables_D = ['D1', 'D2'];
        const tables_Bar = ['Bar1', 'Bar2', 'Bar3', 'Bar4', 'Bar5', 'Bar6', 'Bar7', 'Bar8', ];
        const tables_O = ['O1', 'O2', 'O3', 'O4', 'O5', 'O6', 'O7', 'O8'];
	        const tableGroups = [
	          { key: 'A', shortLabel: 'A', label: 'A tables', tables: tables_A },
	          { key: 'B', shortLabel: 'B', label: 'B tables', tables: tables_B },
	          { key: 'C', shortLabel: 'C', label: 'C tables', tables: tables_C },
	          { key: 'D', shortLabel: 'D', label: 'D tables', tables: tables_D },
	          { key: 'Bar', shortLabel: 'Bar', label: 'Bar seating', tables: tables_Bar },
	          { key: 'O', shortLabel: 'Out', label: 'Outdoor tables', tables: tables_O },
	        ];

	        const table_status = ['active', 'void', 'complete'];
	        const reservation_status = ['active', 'noticed', 'complete', 'void'];
	        const status_labels = { active: 'Active', noticed: 'Notified', complete: 'Complete', void: 'Void' };
	        const reservationRows = this.getReservationRows();
	        const waitlistRows = this.getWaitlistRows();
	        const floorSummary = this.getFloorSummary();
	        const operator = (this.state.adminSession && this.state.adminSession.user) || {};
	        const operatorName = operator.fullName || operator.firstname || 'Front Desk';
	        const { uiDialog, toast } = this.state;
	        const toastIcon = toast && (toast.variant === 'success'
	          ? 'check'
	          : toast.variant === 'info' ? 'info' : 'warning');

	        return (
	          <div className="tm-app-shell">
              <div className="tm-ambient tm-ambient--one" aria-hidden="true" />
              <div className="tm-ambient tm-ambient--two" aria-hidden="true" />

              <header className="tm-topbar">
                <div className="tm-brand">
                  <span className="tm-brand-mark">E</span>
                  <span className="tm-brand-copy">
                    <strong>Evergreen</strong>
                    <small>Host Console</small>
                  </span>
                </div>
                <div className="tm-topbar-center">
                  <span className="tm-live-pill"><i />Live service</span>
                  <span className="tm-date-label">{format(detroitNowPicker(), "EEEE, MMMM d")}</span>
                </div>
                <div className="tm-account">
                  <span className="tm-account-avatar">{operatorName.charAt(0).toUpperCase()}</span>
                  <span className="tm-account-copy"><small>Signed in as</small><strong>{operatorName}</strong></span>
                  <button type="button" className="tm-signout" onClick={this.clearAdminSession}>
                    <AppIcon name="logout" /><span>Sign out</span>
                  </button>
                </div>
              </header>

              <main className="tm-main">
                <section className="tm-floor-command">
                  <div className="tm-floor-command__heading">
                    <span className="tm-eyebrow">Dining room</span>
                    <h1>Today’s floor</h1>
                    <p>Seat guests and keep service moving.</p>
                  </div>

                  <div className="tm-stat-grid" aria-label="Current service summary">
                    <div className="tm-stat-card tm-stat-card--primary">
                      <span className="tm-stat-icon"><AppIcon name="users" /></span>
                      <span><small>Active now</small><strong>{floorSummary.activeGuests}</strong><em>{floorSummary.activeTables} seated</em></span>
                    </div>
                    <button type="button" className="tm-stat-card" onClick={()=>this.toggleReservationModal()} disabled={!reservationRows.length}>
                      <span className="tm-stat-icon"><AppIcon name="calendar" /></span>
                      <span><small>Reservations</small><strong>{reservationRows.length}</strong><em>Upcoming</em></span>
                    </button>
                    <button type="button" className="tm-stat-card tm-stat-card--wait" onClick={()=>this.toggleWaitlistModal()} disabled={!waitlistRows.length}>
                      <span className="tm-stat-icon"><AppIcon name="clock" /></span>
                      <span><small>Waitlist</small><strong>{waitlistRows.length}</strong><em>In queue</em></span>
                    </button>
                    <div className="tm-stat-card">
                      <span className="tm-stat-icon"><AppIcon name="layers" /></span>
                      <span><small>Sections</small><strong>{floorSummary.sections}</strong><em>{floorSummary.completedGuests} completed</em></span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className={`tm-filter-button ${this.state.showActivateTableOnly ? 'is-active' : ''}`}
                    onClick={()=>{this.setState({showActivateTableOnly: this.state.showActivateTableOnly?false:true})}}
                    aria-pressed={this.state.showActivateTableOnly}
                  >
                    <AppIcon name="eye" />
                    <span>{this.state.showActivateTableOnly ? 'Active only' : 'All tables'}</span>
                  </button>
                </section>

                <section className="tm-floor-panel">
                  <div className="tm-floor-panel__header">
                    <div>
                      <span className="tm-eyebrow">Floor map</span>
                      <div className="tm-floor-panel__title">
                        <h2>Table sections</h2>
                        <span>{floorSummary.sections} total</span>
                      </div>
                    </div>
                    <div className="tm-legend" aria-label="Table status legend">
                      <span><i className="tm-dot tm-dot--active" />Active</span>
                      <span><i className="tm-dot tm-dot--complete" />Complete</span>
                      <span><i className="tm-dot tm-dot--void" />Void</span>
                    </div>
                  </div>
                  <div className="tm-sections-grid">
                    {this.generateSectionData()}
                  </div>
                </section>
              </main>

              <nav className="tm-action-dock" aria-label="Front desk actions">
                <button type="button" className="tm-dock-action" onClick={()=>{this.setState({showActivateTableOnly: this.state.showActivateTableOnly?false:true})}}>
                  <span><AppIcon name="eye" /></span><small>{this.state.showActivateTableOnly?'Show all':'Active only'}</small>
                </button>
                <button type="button" className="tm-dock-action tm-dock-action--reservation" onClick={()=>this.handleNewAppointmentModel()}>
                  <span><AppIcon name="calendar" /></span><small>Reservation</small>
                </button>
                <button type="button" className="tm-dock-action tm-dock-action--waitlist" onClick={()=>this.handleNewWaitlistModel()}>
                  <span><AppIcon name="clock" /></span><small>Waitlist</small>
                </button>
                <button type="button" className="tm-dock-action tm-dock-action--complete" onClick={()=>this.completeAllActive()}>
                  <span><AppIcon name="check" /></span><small>Complete all</small>
                </button>
                <button type="button" className="tm-dock-action" onClick={()=>this.addSection()}>
                  <span><AppIcon name="layers" /></span><small>Add sections</small>
                </button>
                <button type="button" className="tm-dock-action tm-dock-action--danger" onClick={()=>this.resetApp()}>
                  <span><AppIcon name="reset" /></span><small>Reset floor</small>
                </button>
              </nav>

            <Modal show={this.state.show} className="tm-modal tm-section-setup" centered backdrop="static">
              <Modal.Header>
                <Modal.Title>
                  <span className="tm-modal-title-icon"><AppIcon name="layers" /></span>
                  <span><small>Floor setup</small>How many sections today?</span>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                  <p className="tm-section-setup-copy">Choose the number of floor sections you want to manage for this service.</p>
                  <div className="tm-section-picker">
                    {[1, 2, 3, 4, 5, 6].map((sectionCount) => (
                      <Button key={sectionCount} variant="outline-success" size="lg" onClick = {()=>this.setSection(sectionCount)}>
                        <strong>{sectionCount}</strong><small>{sectionCount === 1 ? 'section' : 'sections'}</small>
                      </Button>
                    ))}
                  </div>
              </Modal.Body>
              <Modal.Footer>
              </Modal.Footer>
            </Modal>


            <Modal show={this.state.editShow} className="tm-modal tm-seat-modal" centered size="lg">
              <Modal.Header>
                <Modal.Title>
                  <span className="tm-modal-title-icon"><AppIcon name="edit" /></span>
                  <span><small>Section {this.state.selectedSection}</small>Edit table information</span>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="tm-seat-modal__body">
                <div className="tm-seated-time">Seated at <strong>{this.state.tableTime}</strong></div>
                <TableSelector
                  groups={tableGroups}
                  activeGroupKey={this.state.editTableZone}
                  onGroupChange={(editTableZone) => this.setState({ editTableZone })}
                  selectedTable={this.state.selectedTableEdit}
                  onSelectTable={(selectedTableEdit) => this.setState({ selectedTableEdit })}
                  idPrefix="edit-party"
                />
                <div className="tm-seat-details-grid">
                  <div className="tm-seat-detail-card tm-seat-detail-card--guest">
                    <label htmlFor="edit-party-size"><small>Party details</small><strong>Guest count</strong></label>
                    <GuestCountPicker
                      compact
                      id="edit-party-size"
                      value={this.state.peopleEditEnter}
                      onChange={(peopleEditEnter) => this.setState({ peopleEditEnter })}
                    />
                  </div>
                  <div className="tm-seat-detail-card">
                    <div className="tm-seat-detail-label"><small>Service state</small><strong>Table status</strong></div>
                    <div className="tm-status-selector" role="group" aria-label="Table status">
                      {table_status.map((status) => (
                        <button key={status} type="button" className={`tm-status-choice tm-status-${status} ${this.state.tableStatus === status ? 'is-selected' : ''}`} aria-pressed={this.state.tableStatus === status} onClick={() => this.setState({ tableStatus: status })}>
                          <i className={`tm-dot tm-dot--${status}`} />{status_labels[status] || status}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <span className="tm-modal-footer-copy">Editing table {this.state.selectedTableEdit}</span>
                <Button variant="secondary" onClick={()=>this.handleEditModel('')}>Cancel</Button>
                <Button variant="primary" onClick={()=>this.editRecordAction()}>Save changes</Button>
              </Modal.Footer>
            </Modal>


            <Modal show={this.state.addShow} className="tm-modal tm-seat-modal" centered size="lg">
              <Modal.Header>
                <Modal.Title>
                  <span className="tm-modal-title-icon"><AppIcon name="users" /></span>
                  <span><small>Section {this.state.curSection}</small>Seat a new party</span>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="tm-seat-modal__body">
                <TableSelector
                  groups={tableGroups}
                  activeGroupKey={this.state.addTableZone}
                  onGroupChange={(addTableZone) => this.setState({ addTableZone })}
                  selectedTable={this.state.tableOption}
                  onSelectTable={(tableOption) => this.setState({ tableOption })}
                  idPrefix="new-party"
                />
                <section className="tm-guest-count-section" aria-labelledby="new-party-count-title">
                  <div className="tm-guest-count-heading">
                    <span className="tm-form-step">2</span>
                    <span><small>Party details</small><strong id="new-party-count-title">How many guests?</strong></span>
                  </div>
                  <GuestCountPicker
                    id="new-party-size"
                    value={this.state.peopleEnter}
                    onChange={(peopleEnter) => this.setState({ peopleEnter })}
                  />
                </section>
              </Modal.Body>
              <Modal.Footer>
                <span className={`tm-modal-footer-copy ${this.state.tableOption === 'Choose a Table' ? 'is-empty' : ''}`}>
                  {this.state.tableOption === 'Choose a Table' ? 'Choose a table to continue' : `Table ${this.state.tableOption} selected`}
                </span>
                <Button variant="secondary" onClick={()=>this.handleAddModel('')}>Cancel</Button>
                <Button variant="primary" onClick={()=>this.addRecord()}>Seat party</Button>
              </Modal.Footer>
            </Modal>


            {/* <Modal show={this.state.showEditModal}>
              <Modal.Header>
                <Modal.Title>Change Table Status: </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                  <div className="d-grid gap-2">
                    <Button variant="light" size="lg" onClick = {()=>this.editRecordAction('active')}>Active Table</Button>
                    <Button variant="secondary" size="lg" onClick = {()=>this.editRecordAction('void')}>Void Table</Button>
                    <Button variant="success" size="lg" onClick = {()=>this.editRecordAction('complete')}>Complete Table</Button>
                  </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="default" onClick={()=>this.editRecord('','')}>Cancel</Button>
              </Modal.Footer>
            </Modal> */}


            <Modal show={this.state.showNewReservationModal} className="tm-modal tm-form-modal" centered size="lg">
              <Modal.Header>
                <Modal.Title>
                  <span className="tm-modal-title-icon"><AppIcon name="calendar" /></span>
                  <span><small>New booking</small>Reservation details</span>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
              
                <Col className="text-center">
                  <DatePicker 
                    wrapperClassName="datepicker"
                    selected={this.state.selectedData}
                    onChange={(date) => this.setState({selectedData: date})}
                    minDate={reservationDateRange().minDate}
                    maxDate={reservationDateRange().maxDate}
                    placeholderText="Select a date between today and 30 days in the future"
                    showTimeSelect
                    timeIntervals={15}
                    filterTime={(time) => isReservationTimeAllowed(this.state.selectedData, time)}
                    dateFormat="MMMM d, yyyy hh:mm aa"
                    inline
                  />
                </Col>

                <Col>
                  <InputGroup className="mb-3">
                   <InputGroup.Text>Date & Time (Detroit)</InputGroup.Text>
                   <Form.Control value={format(this.state.selectedData, "MM/dd/yyyy, hh:mm aa")} disabled/>
                   </InputGroup>


                  <InputGroup className="mb-3">
                   <InputGroup.Text>Name</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="Name" value={this.state.reservationName} onChange={(e)=>this.setState({reservationName: e.target.value})}/>
                   </InputGroup>

                   <InputGroup className="mb-3">
                   <InputGroup.Text>Group Size</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="How many people" value={this.state.reservationSize} onChange={(e)=>this.setState({reservationSize: e.target.value})} type="number"/>
                   </InputGroup>       

                   <InputGroup className="mb-3">
                   <InputGroup.Text>Phone Number</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="Phone number" value={this.state.reservationPhone} onChange={(e)=>this.setState({reservationPhone: e.target.value})} type="number"/>
                   </InputGroup>    

                   <InputGroup className="mb-3">
                   <InputGroup.Text>Note</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="Note" value={this.state.reservationNote} onChange={(e)=>this.setState({reservationNote: e.target.value})} type="text"/>
                   </InputGroup>             
                </Col>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={()=>this.handleNewAppointmentModel(false)}>Cancel</Button>
                <Button variant="primary" onClick={()=>this.handleNewAppointmentAction()}>Save</Button>
              </Modal.Footer>
            </Modal>


            <Modal show={this.state.showNewWaitlistModal} className="tm-modal tm-form-modal" centered>
              <Modal.Header>
                <Modal.Title>
                  <span className="tm-modal-title-icon tm-modal-title-icon--wait"><AppIcon name="clock" /></span>
                  <span><small>Live queue</small>Add to waitlist</span>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Col>
                  <InputGroup className="mb-3">
                   <InputGroup.Text>Date & Time (Detroit)</InputGroup.Text>
                   <Form.Control value={format(this.state.waitlistSelectedData, "MM/dd/yyyy, hh:mm aa")} disabled/>
                   </InputGroup>


                  <InputGroup className="mb-3">
                   <InputGroup.Text>Name</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="Name" value={this.state.waitlistName} onChange={(e)=>this.setState({waitlistName: e.target.value})}/>
                   </InputGroup>

                   <InputGroup className="mb-3">
                   <InputGroup.Text>Group Size</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="How many people" value={this.state.waitlistSize} onChange={(e)=>this.setState({waitlistSize: e.target.value})} type="number"/>
                   </InputGroup>       

                   <InputGroup className="mb-3">
                   <InputGroup.Text>Phone Number</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="Phone number" value={this.state.waitlistPhone} onChange={(e)=>this.setState({waitlistPhone: e.target.value})} type="number"/>
                   </InputGroup>    

                   <InputGroup className="mb-3">
                   <InputGroup.Text>Note</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="Note" value={this.state.waitlistNote} onChange={(e)=>this.setState({waitlistNote: e.target.value})} type="text"/>
                   </InputGroup>             
                </Col>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={()=>this.handleNewWaitlistModel(false)}>Cancel</Button>
                <Button variant="primary" onClick={()=>this.handleNewWaitlistAction()}>Save</Button>
              </Modal.Footer>
            </Modal>


            <Modal show={this.state.showReservations} className="tm-modal tm-list-modal" centered size="lg">
              <Modal.Header>
                <Modal.Title>
                  <span className="tm-modal-title-icon"><AppIcon name="calendar" /></span>
                  <span><small>Today &amp; tomorrow</small>Upcoming reservations</span>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="tm-guest-list">
                {this.renderGuestRows(reservationRows, 'reservation')}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={()=>this.toggleReservationModal()}>Close</Button>
              </Modal.Footer>
            </Modal>


            <Modal show={this.state.showWaitlist} className="tm-modal tm-list-modal" centered size="lg">
              <Modal.Header>
                <Modal.Title>
                  <span className="tm-modal-title-icon tm-modal-title-icon--wait"><AppIcon name="clock" /></span>
                  <span><small>Live queue</small>Waitlist</span>
                </Modal.Title>
              </Modal.Header>
	              <Modal.Body className="tm-guest-list">
                  {this.renderGuestRows(waitlistRows, 'waitlist')}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={()=>this.toggleWaitlistModal()}>Close</Button>
              </Modal.Footer>
            </Modal>



            <Modal show={this.state.showEditReservationModal} className="tm-modal tm-form-modal" centered size="lg">
              <Modal.Header>
                <Modal.Title>
                  <span className="tm-modal-title-icon"><AppIcon name="edit" /></span>
                  <span><small>Reservation</small>Update guest details</span>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
              
                <Col className="text-center">
                  <DatePicker 
                    wrapperClassName="datepicker"
                    selected={this.state.editSelectedData}
                    onChange={(date) => this.setState({editSelectedData: date})}
                    minDate={reservationDateRange().minDate}
                    maxDate={reservationDateRange().maxDate}
                    placeholderText="Select a date between today and 30 days in the future"
                    showTimeSelect
                    timeIntervals={15}
                    filterTime={(time) => isReservationTimeAllowed(this.state.editSelectedData, time)}
                    dateFormat="MMMM d, yyyy hh:mm aa"
                    inline
                  />
                </Col>

                <Col>
                  <InputGroup className="mb-3">
                   <InputGroup.Text>Date & Time (Detroit)</InputGroup.Text>
                   <Form.Control value={format(this.state.editSelectedData, "MM/dd/yyyy, hh:mm aa")} disabled/>
                   </InputGroup>

                   <InputGroup className="mb-3 tm-status-group">
                   <InputGroup.Text>Status</InputGroup.Text>
                   <div className="tm-status-selector" role="group" aria-label="Reservation status">
                     {reservation_status.map((status) => (
                       <button key={status} type="button" className={`tm-status-choice tm-status-${status} ${this.state.editReservationStatus === status ? 'is-selected' : ''}`} aria-pressed={this.state.editReservationStatus === status} onClick={() => this.setState({ editReservationStatus: status })}>
                         <i className={`tm-dot tm-dot--${status}`} />{status_labels[status] || status}
                       </button>
                     ))}
                   </div>
                   </InputGroup>


                  <InputGroup className="mb-3">
                   <InputGroup.Text>Name</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="Name" value={this.state.editReservationName} onChange={(e)=>this.setState({editReservationName: e.target.value})}/>
                   </InputGroup>

                   <InputGroup className="mb-3">
                   <InputGroup.Text>Group Size</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="How many people" value={this.state.editReservationSize} onChange={(e)=>this.setState({editReservationSize: e.target.value})} type="number"/>
                   </InputGroup>       

                   <InputGroup className="mb-3">
                   <InputGroup.Text>Phone Number</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="Phone number" value={this.state.editReservationPhone} onChange={(e)=>this.setState({editReservationPhone: e.target.value})} type="number"/>
                   </InputGroup>    

                   <InputGroup className="mb-3">
                   <InputGroup.Text>Note</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="Note" value={this.state.editReservationNote} onChange={(e)=>this.setState({editReservationNote: e.target.value})} type="text"/>
                   </InputGroup>             
                </Col>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={()=>this.toggleEditReservationModal()}>Cancel</Button>
                <Button variant="primary" onClick={()=>this.updateReservation()}>Update</Button>
              </Modal.Footer>
            </Modal>


            <Modal show={this.state.showEditWaitlistModal} className="tm-modal tm-form-modal" centered>
              <Modal.Header>
                <Modal.Title>
                  <span className="tm-modal-title-icon tm-modal-title-icon--wait"><AppIcon name="edit" /></span>
                  <span><small>Waitlist</small>Update guest details</span>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Col>
                  <InputGroup className="mb-3">
                   <InputGroup.Text>Date & Time (Detroit)</InputGroup.Text>
                   <Form.Control value={format(this.state.editWaitlistSelectedData, "MM/dd/yyyy, hh:mm aa")} disabled/>
                   </InputGroup>

                   <InputGroup className="mb-3 tm-status-group">
                   <InputGroup.Text>Status</InputGroup.Text>
                   <div className="tm-status-selector" role="group" aria-label="Waitlist status">
                     {reservation_status.map((status) => (
                       <button key={status} type="button" className={`tm-status-choice tm-status-${status} ${this.state.editWaitlistStatus === status ? 'is-selected' : ''}`} aria-pressed={this.state.editWaitlistStatus === status} onClick={() => this.setState({ editWaitlistStatus: status })}>
                         <i className={`tm-dot tm-dot--${status}`} />{status_labels[status] || status}
                       </button>
                     ))}
                   </div>
                   </InputGroup>

                  <InputGroup className="mb-3">
                   <InputGroup.Text>Name</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="Name" value={this.state.editWaitlistName} onChange={(e)=>this.setState({editWaitlistName: e.target.value})}/>
                   </InputGroup>

                   <InputGroup className="mb-3">
                   <InputGroup.Text>Group Size</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="How many people" value={this.state.editWaitlistSize} onChange={(e)=>this.setState({editWaitlistSize: e.target.value})} type="number"/>
                   </InputGroup>       

                   <InputGroup className="mb-3">
                   <InputGroup.Text>Phone Number</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="Phone number" value={this.state.editWaitlistPhone} onChange={(e)=>this.setState({editWaitlistPhone: e.target.value})} type="number"/>
                   </InputGroup>    

                   <InputGroup className="mb-3">
                   <InputGroup.Text>Note</InputGroup.Text>
                   <Form.Control aria-label="table people count" placeholder="Note" value={this.state.editWaitlistNote} onChange={(e)=>this.setState({editWaitlistNote: e.target.value})} type="text"/>
                   </InputGroup>             
                </Col>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={()=>this.toggleEditWaitlistModal()}>Cancel</Button>
                <Button variant="primary" onClick={()=>this.updateWaitlist()}>Update</Button>
              </Modal.Footer>
            </Modal>

            <Modal
              show={Boolean(uiDialog)}
              className={`tm-modal tm-system-dialog ${uiDialog && uiDialog.kind === 'danger' ? 'tm-system-dialog--danger' : ''}`}
              backdropClassName="tm-system-dialog-backdrop"
              centered
              backdrop="static"
              keyboard={!this.state.dialogBusy}
              onHide={this.closeDialog}
            >
              {uiDialog ? (
                <>
                  <Modal.Header>
                    <Modal.Title>
                      <span className="tm-modal-title-icon"><AppIcon name={uiDialog.icon || 'info'} /></span>
                      <span><small>{uiDialog.eyebrow}</small>{uiDialog.title}</span>
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <p className="tm-system-dialog__message">{uiDialog.message}</p>
                    {uiDialog.kind === 'input' ? (
                      <Form.Group className="tm-dialog-field" controlId="tm-dialog-input">
                        <Form.Label>{uiDialog.inputLabel}</Form.Label>
                        <Form.Control
                          autoFocus
                          type={uiDialog.inputType || 'text'}
                          min={uiDialog.inputType === 'number' ? '1' : undefined}
                          step={uiDialog.inputType === 'number' ? '1' : undefined}
                          inputMode={uiDialog.inputType === 'number' ? 'numeric' : undefined}
                          placeholder={uiDialog.placeholder}
                          value={this.state.dialogValue}
                          onChange={this.handleDialogValue}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              this.confirmDialog();
                            }
                          }}
                          isInvalid={Boolean(this.state.dialogError)}
                        />
                      </Form.Group>
                    ) : null}
                    {this.state.dialogError ? (
                      <div className="tm-dialog-error" role="alert">
                        <AppIcon name="warning" />
                        <span>{this.state.dialogError}</span>
                      </div>
                    ) : null}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="secondary" onClick={this.closeDialog} disabled={this.state.dialogBusy}>
                      {uiDialog.cancelLabel || 'Cancel'}
                    </Button>
                    <Button
                      variant="primary"
                      className={uiDialog.kind === 'danger' ? 'tm-dialog-confirm--danger' : ''}
                      onClick={this.confirmDialog}
                      disabled={this.state.dialogBusy}
                    >
                      {this.state.dialogBusy ? 'Working…' : uiDialog.confirmLabel || 'Continue'}
                    </Button>
                  </Modal.Footer>
                </>
              ) : null}
            </Modal>

            <div className="tm-toast-region" aria-live="polite" aria-atomic="true">
              {toast ? (
                <div
                  key={toast.id}
                  className={`tm-toast tm-toast--${toast.variant}`}
                  role={toast.variant === 'error' || toast.variant === 'warning' ? 'alert' : 'status'}
                >
                  <span className="tm-toast__icon"><AppIcon name={toastIcon} /></span>
                  <span className="tm-toast__copy">
                    <strong>{toast.title}</strong>
                    <small>{toast.message}</small>
                  </span>
                  <button type="button" className="tm-toast__close" onClick={this.dismissToast} aria-label="Dismiss notification">×</button>
                  <span className="tm-toast__timer" aria-hidden="true" />
                </div>
              ) : null}
            </div>


	          </div>
      );
  }
}

export default App;
